import { createElement, getUiRoot, setText } from './dom';
import { setLabel, translate } from './i18n';
import { chatLength, chatText } from '../multiplayer/chatSender';
import { createIcon, type UiIcon } from './icons';

function chatButton(parent: HTMLElement, label: string, icon: UiIcon): HTMLButtonElement {
  const button = createElement('button', 'conversation-close', parent);
  setLabel(button, 'aria-label', label);
  setLabel(button, 'title', label);
  const image = createIcon(icon, 16);
  image.setAttribute('aria-hidden', 'true');
  image.setAttribute('stroke-width', '1.5');
  button.append(image);
  return button;
}

export interface WhisperTarget {
  readonly id: string;
  readonly name: string;
}

class ChatPanel {
  private readonly dialog = createElement('dialog', 'chat-composer', getUiRoot());
  private readonly form = createElement('form', 'chat-form', this.dialog);
  private readonly recipient = createElement('p', 'chat-recipient', this.form);
  private readonly row = createElement('div', 'chat-compose', this.form);
  private readonly input = createElement('input', 'chat-input', this.row);
  private readonly count = createElement('span', 'chat-count', this.row);
  private readonly send = chatButton(this.row, 'Send message', 'send');
  private readonly close = chatButton(this.row, 'Close conversation', 'close');
  private readonly error = createElement('p', 'chat-error', this.form);
  private target: WhisperTarget | null = null;
  private sending = false;
  private composing = false;

  constructor(
    private readonly openChanged: (open: boolean) => void,
    private readonly say: (text: string, recipient: string | null) => Promise<void>
  ) {
    this.send.type = 'submit';
    this.close.type = 'button';
    this.close.addEventListener('click', () => {
      this.hide();
    });
    this.input.id = 'chat-words';
    this.input.type = 'text';
    this.input.maxLength = 160;
    this.input.autocomplete = 'off';
    this.input.enterKeyHint = 'send';
    this.input.setAttribute('aria-describedby', 'chat-count chat-error');
    this.count.id = 'chat-count';
    this.error.id = 'chat-error';
    this.error.hidden = true;
    this.recipient.hidden = true;
    setLabel(this.dialog, 'aria-label', 'Speak to nearby visitors');
    setLabel(this.input, 'aria-label', 'Your words');
    this.input.addEventListener('compositionstart', () => {
      this.composing = true;
    });
    this.input.addEventListener('compositionend', () => {
      this.composing = false;
      this.limit();
    });
    this.input.addEventListener('input', () => {
      if (!this.composing) this.limit();
    });
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      if (!this.composing) void this.submit();
    });
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.hide();
    });
    this.limit();
  }

  get isOpen(): boolean {
    return this.dialog.open;
  }

  show(target: WhisperTarget | null = null): void {
    if (this.isOpen) return;
    this.target = target;
    this.recipient.hidden = !target;
    this.recipient.textContent = target ? `${translate('Whisper')} · ${target.name}` : '';
    this.error.hidden = true;
    this.input.removeAttribute('aria-invalid');
    this.openChanged(true);
    this.dialog.showModal();
    this.input.focus();
  }

  hide(): void {
    if (!this.isOpen) return;
    this.dialog.close();
    this.target = null;
    this.openChanged(false);
  }

  dispose(): void {
    this.hide();
    this.dialog.remove();
  }

  private limit(): void {
    const chars = Array.from(this.input.value);
    if (chars.length > 80) this.input.value = chars.slice(0, 80).join('');
    this.count.textContent = `${chatLength(this.input.value)} / 80`;
    this.send.disabled = this.sending || !this.input.value.trim();
  }

  private async submit(): Promise<void> {
    if (this.sending) return;
    if (!this.input.value.trim()) {
      this.hide();
      return;
    }
    this.error.hidden = true;
    this.input.removeAttribute('aria-invalid');
    try {
      const text = chatText(this.input.value);
      this.sending = true;
      this.send.disabled = true;
      this.input.disabled = true;
      this.dialog.setAttribute('aria-busy', 'true');
      await this.say(text, this.target?.id ?? null);
      this.input.value = '';
      this.limit();
      this.hide();
    } catch (reason) {
      setText(
        this.error,
        reason instanceof Error ? reason.message : 'Your words could not reach the city.'
      );
      this.error.setAttribute('role', 'alert');
      this.error.hidden = false;
      this.input.setAttribute('aria-invalid', 'true');
    } finally {
      this.sending = false;
      this.limit();
      this.input.disabled = false;
      this.dialog.removeAttribute('aria-busy');
      if (this.isOpen) this.input.focus();
    }
  }
}

export function createChatPanel(
  openChanged: (open: boolean) => void,
  say: (text: string, recipient: string | null) => Promise<void>
) {
  return new ChatPanel(openChanged, say);
}
